import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FiCornerUpLeft, FiMaximize2, FiPlay, FiRefreshCw, FiX } from 'react-icons/fi';
import { motionReviewApi, readableError } from './api';
import MotionSpottedTime from './MotionSpottedTime';
import MotionAnswerPanel from './MotionAnswerPanel';
import InlineFullClip from './InlineFullClip';
import BufferedMotionReviewScreen from './BufferedMotionReviewScreen';
import { MotionFlagActions, MotionMobileActions, MotionReviewRules, MotionReviewNotice } from './MotionReviewHelp';
import MotionTrackReference from './MotionTrackReference';
import MotionCelebration, { useMotionCelebration } from './MotionCelebration';
import useMotionAutoplay from './useMotionAutoplay';
import { trackCommunity } from './analytics';
import MotionVideoDeck from './MotionVideoDeck';
import { applyMotionPlaybackRate } from './motionPlayback';
import useMotionPlaybackPreference, { getMotionPlaybackRate } from './useMotionPlaybackPreference';
import useMotionBoxPreference from './useMotionBoxPreference';
import MotionLeaderboard from './MotionLeaderboard';

const requestId = () => window.crypto?.randomUUID?.() || `motion-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
const isTyping = (target) => Boolean(target?.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"]), .cr-motion-rules, .cr-motion-mobile-tools, video[controls]'));
const noFlags = () => ({ predator: false, interesting: false });

export function useCompactMotionView() {
  const query = '(max-width: 760px)';
  const [compact, setCompact] = useState(() => window.matchMedia ? window.matchMedia(query).matches : window.innerWidth <= 760);
  useEffect(() => {
    if (!window.matchMedia) {
      const resize = () => setCompact(window.innerWidth <= 760);
      window.addEventListener('resize', resize);
      return () => window.removeEventListener('resize', resize);
    }
    const media = window.matchMedia(query);
    const change = () => setCompact(media.matches);
    change();
    media.addEventListener?.('change', change);
    if (!media.addEventListener) media.addListener?.(change);
    return () => {
      media.removeEventListener?.('change', change);
      if (!media.removeEventListener) media.removeListener?.(change);
    };
  }, []);
  return compact;
}

export function MotionEvidence({ task, onLoad, onError, onMaskError, autoPlay = true, active = true, onEnlarge, onRefresh, showSpottedTime = false, upcomingTasks = null, presentationKey, reviewedCount, accuracy, previewOnly = false, allowFullClip = false }) {
  const video = useRef(null);
  const cleanPreview = useRef(null);
  const maskVideo = useRef(null);
  const presentedTime = useRef(null);
  const compactViewport = useCompactMotionView();
  const compact = previewOnly || compactViewport;
  const mediaKey = `${task.id}|${task.videoUrl}|${presentationKey || ''}`;
  const { bindVideo: bindAutoplayVideo, cancelAutoplay } = useMotionAutoplay(video, { enabled: autoPlay && compact, mediaKey, active });
  const bindVideo = useCallback((element) => {
    applyMotionPlaybackRate(element, getMotionPlaybackRate());
    bindAutoplayVideo(element);
  }, [bindAutoplayVideo]);
  const [controlsFor, setControlsFor] = useState(null);
  const [mobileBoxesShown, setBoxesShown] = useMotionBoxPreference();
  // Desktop keeps a dedicated boxed preview; mobile remembers its toggle.
  const boxesShown = !compact || mobileBoxesShown;
  const nativeControlsShown = previewOnly || !compact || controlsFor === mediaKey;
  const [frameIndex, setFrameIndex] = useState(0);
  const [speed, setSpeed] = useMotionPlaybackPreference();
  const [maskReady, setMaskReady] = useState(false);
  const [maskFailed, setMaskFailed] = useState(false);
  const [frameReadyFor, setFrameReadyFor] = useState(null);
  const [playingFor, setPlayingFor] = useState(null);
  const [playErrorFor, setPlayErrorFor] = useState(null);
  const width = Number(task.frameW) || 640;
  const height = Number(task.frameH) || 360;
  const fps = Number(task.fps) || 25;
  const boxesByFrame = useMemo(() => {
    const frames = new Map();
    for (const box of task.boxes || []) {
      if (![box.frameIndex, box.x1, box.y1, box.x2, box.y2].every(Number.isFinite)) continue;
      const boxes = frames.get(box.frameIndex) || [];
      boxes.push(box);
      frames.set(box.frameIndex, boxes);
    }
    return frames;
  }, [task.boxes]);

  const drawCleanPreview = useCallback(() => {
    const source = video.current;
    const canvas = cleanPreview.current;
    if (compact || !source || !canvas || source.readyState < 2) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    // The same decoded frame feeds both previews. A cross-origin source can
    // taint this display-only canvas; no pixel readback or export is performed.
    context.drawImage(source, 0, 0, width, height);
  }, [compact, height, width]);

  const synchronizeMask = useCallback(({ forceSeek = false } = {}) => {
    const source = video.current;
    const mask = maskVideo.current;
    if (!active) { mask?.pause(); return; }
    if (compact || !task.maskVideoUrl || !source || !mask || mask.readyState < 1) return;
    mask.playbackRate = source.playbackRate;
    const sourceTime = source.paused && !source.seeking && presentedTime.current !== null ? presentedTime.current : source.currentTime;
    const targetTime = Number.isFinite(mask.duration) ? Math.min(sourceTime, Math.max(0, mask.duration - 0.001)) : sourceTime;
    // Normal playback uses the same clock/rate; correct an actual drift, seek
    // or loop instead of issuing an expensive video seek on every frame.
    const drift = Math.abs(mask.currentTime - targetTime);
    const tolerance = source.paused ? 0.5 / fps : Math.max(0.08, 2 / fps);
    if (!mask.seeking && drift > (forceSeek ? 0.0001 : tolerance)) {
      try { mask.currentTime = targetTime; } catch (_) { /* Metadata may be changing during a source refresh. */ }
    }
    if (source.paused || source.ended || source.readyState < 2) {
      if (!mask.paused) mask.pause();
    } else if (mask.paused) {
      mask.play()?.catch(() => { /* A later master play/canplay event retries playback. */ });
    }
  }, [active, compact, fps, task.maskVideoUrl]);

  const setPlaybackSpeed = useCallback((value) => {
    setSpeed(value);
    for (const element of [video.current, maskVideo.current]) {
      applyMotionPlaybackRate(element, value);
    }
  }, [setSpeed]);

  useEffect(() => { setControlsFor(null); }, [mediaKey]);
  const playClip = () => {
    const element = video.current;
    if (!active || !element || !element.paused) return;
    cancelAutoplay();
    setPlayErrorFor(null);
    element.defaultMuted = true;
    element.muted = true;
    element.playsInline = true;
    const failed = () => {
      if (video.current === element) setPlayErrorFor(mediaKey);
    };
    // Keep play() in the tap handler so iOS can use the user gesture when
    // autoplay is blocked, including after returning from another app.
    try { element.play()?.catch(failed); } catch (_) { failed(); }
  };
  const toggleControls = () => {
    trackCommunity('motion_controls_toggled', { controls_visible: !nativeControlsShown, device_layout: compact ? 'mobile' : 'desktop' });
    if (nativeControlsShown) { setControlsFor(null); return; }
    cancelAutoplay();
    setControlsFor(mediaKey);
    // A deliberate tap can start playback if the browser blocked autoplay.
    // Keep this call inside the gesture; the revealed native controls remain
    // available if playback is still rejected.
    if (video.current?.paused) {
      try { video.current.play()?.catch(() => {}); } catch (_) { /* Native Play is available for retry. */ }
    }
  };

  useEffect(() => { setMaskReady(false); setMaskFailed(false); }, [compact, task.maskVideoUrl]);
  useEffect(() => {
    const stage = video.current?.closest('.cr-motion-stage');
    const previews = stage?.closest('.cr-motion-previews');
    const main = stage?.closest('.cr-motion-main');
    if (!main || stage.closest('[role="dialog"]')) return undefined;
    const answers = main.querySelector('.cr-answer-panel');
    const reference = main.querySelector('.cr-motion-track-reference');
    // Full-recording availability correlates with the model decision. Only
    // staff details opt in; community voting must have identical controls/space.
    const fullClipToggle = allowFullClip ? main.querySelector('.cr-inline-full-clip-toggle') : null;
    const videoTools = main.querySelector('.cr-motion-video-tools');
    const fit = () => {
      // Reserve the fixed phone answer bar's real height. Use document position
      // so scrolling does not resize the video or make the page jump.
      const top = stage.getBoundingClientRect().top + window.scrollY;
      const referenceHeight = reference ? reference.getBoundingClientRect().height + 12 : 0;
      const docked = compact || window.innerHeight <= 500 || (answers && window.getComputedStyle(answers).position === 'fixed');
      const fullClipHeight = fullClipToggle ? fullClipToggle.getBoundingClientRect().height + 22 : 0;
      const videoToolsHeight = compact && videoTools ? videoTools.getBoundingClientRect().height + 12 : 0;
      const reservedHeight = docked ? (answers?.getBoundingClientRect().height || 0) + referenceHeight + fullClipHeight + videoToolsHeight + 12 : Math.max(100, referenceHeight + 50);
      const viewportHeight = compact ? window.visualViewport?.height || window.innerHeight : window.innerHeight;
      const height = `${Math.max(56, viewportHeight - top - reservedHeight)}px`;
      if (previews.style.getPropertyValue('--cr-motion-available-height') !== height) {
        previews.style.setProperty('--cr-motion-available-height', height);
      }
    };
    fit();
    // Changing the observed layout inside ResizeObserver causes WebKit to
    // report a resize loop. Coalesce sizing changes into the next frame.
    let fitFrame = null;
    const scheduleFit = () => {
      if (fitFrame !== null) return;
      fitFrame = window.requestAnimationFrame(() => { fitFrame = null; fit(); });
    };
    const observer = window.ResizeObserver ? new window.ResizeObserver(scheduleFit) : null;
    observer?.observe(main);
    if (answers) observer?.observe(answers);
    if (reference) observer?.observe(reference);
    window.addEventListener('resize', scheduleFit);
    window.visualViewport?.addEventListener('resize', scheduleFit);
    return () => {
      observer?.disconnect();
      if (fitFrame !== null) window.cancelAnimationFrame(fitFrame);
      window.removeEventListener('resize', scheduleFit);
      window.visualViewport?.removeEventListener('resize', scheduleFit);
      previews.style.removeProperty('--cr-motion-available-height');
    };
  }, [allowFullClip, compact, task.videoUrl, nativeControlsShown]);
  useEffect(() => { presentedTime.current = null; }, [compact, task.videoUrl]);
  useLayoutEffect(() => {
    for (const element of [video.current, maskVideo.current]) applyMotionPlaybackRate(element, speed);
  }, [speed, mediaKey, compact]);
  useEffect(() => {
    const element = video.current;
    if (!element || (compact && !boxesShown)) return undefined;
    let active = true;
    let callback;
    let animation;
    const update = (time) => {
      if (boxesShown) setFrameIndex(Math.max(0, Math.floor(time * fps + 0.0001)));
      drawCleanPreview();
      synchronizeMask();
    };
    const videoFrame = (_, metadata) => {
      if (!active) return;
      presentedTime.current = metadata.mediaTime;
      update(metadata.mediaTime);
      callback = element.requestVideoFrameCallback(videoFrame);
    };
    const tick = () => {
      if (!active) return;
      update(element.currentTime);
      if (!element.paused && !element.ended) animation = window.requestAnimationFrame(tick);
    };
    const start = () => {
      if (element.requestVideoFrameCallback) return;
      window.cancelAnimationFrame(animation);
      tick();
    };
    const seek = () => {
      update(element.currentTime);
      synchronizeMask({ forceSeek: true });
    };
    const timeUpdate = () => { if (!element.requestVideoFrameCallback || element.paused) update(element.currentTime); };
    update(element.currentTime);
    if (element.requestVideoFrameCallback) callback = element.requestVideoFrameCallback(videoFrame);
    else if (!element.paused) start();
    element.addEventListener('play', start);
    element.addEventListener('timeupdate', timeUpdate);
    element.addEventListener('seeked', seek);
    return () => {
      active = false;
      if (callback !== undefined) element.cancelVideoFrameCallback?.(callback);
      window.cancelAnimationFrame(animation);
      element.removeEventListener('play', start);
      element.removeEventListener('timeupdate', timeUpdate);
      element.removeEventListener('seeked', seek);
    };
  }, [boxesShown, compact, drawCleanPreview, fps, synchronizeMask, task.videoUrl]);
  const pauseMask = () => { if (maskVideo.current && !maskVideo.current.paused) maskVideo.current.pause(); };
  const frameBoxes = boxesByFrame.get(frameIndex) || [];
  const useDeck = Boolean(compact && upcomingTasks);
  const videoProps = {
    'aria-label': 'Motion review clip', autoPlay: autoPlay && active, muted: true, playsInline: true, loop: true, controls: nativeControlsShown,
    controlsList: 'nofullscreen nodownload noremoteplayback', disablePictureInPicture: true, preload: autoPlay ? 'auto' : 'metadata',
    onLoadedMetadata: (event) => applyMotionPlaybackRate(event.currentTarget, getMotionPlaybackRate()),
    onLoadedData: (event) => { setFrameReadyFor(mediaKey); drawCleanPreview(); synchronizeMask({ forceSeek: true }); onLoad?.(event); },
    onPlaying: () => { setPlayingFor(mediaKey); setPlayErrorFor(null); },
    onClick: compact && !nativeControlsShown ? playClip : undefined,
    onPlay: () => synchronizeMask({ forceSeek: true }),
    onPause: () => { setPlayingFor(null); pauseMask(); synchronizeMask({ forceSeek: true }); }, onWaiting: pauseMask,
    onSeeking: () => { pauseMask(); synchronizeMask({ forceSeek: true }); },
    onCanPlay: () => synchronizeMask({ forceSeek: true }),
    onRateChange: (event) => {
      const element = event.currentTarget;
      // Loading a new source can emit a temporary 1x reset. Only a ready,
      // current player can change the preference through native controls.
      if (element === video.current && element.readyState >= 1 && element.playbackRate !== getMotionPlaybackRate()) setPlaybackSpeed(element.playbackRate);
      synchronizeMask();
    }, onError,
  };
  const boxOverlay = boxesShown && (!compact || frameReadyFor === null || frameReadyFor === mediaKey) && <svg className="cr-motion-boxes" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" aria-hidden="true">{frameBoxes.map((box, index) => {
    const originalLeft = Math.max(0, Math.min(width, box.x1));
    const originalTop = Math.max(0, Math.min(height, box.y1));
    const originalWidth = Math.max(0, Math.min(width, box.x2) - originalLeft);
    const originalHeight = Math.max(0, Math.min(height, box.y2) - originalTop);
    if (!originalWidth || !originalHeight) return null;
    // Display-only padding keeps the box away from small movements.
    const paddingX = Math.max(4, originalWidth * 0.125);
    const paddingY = Math.max(4, originalHeight * 0.125);
    const left = Math.max(0, originalLeft - paddingX);
    const top = Math.max(0, originalTop - paddingY);
    const boxWidth = Math.min(width, originalLeft + originalWidth + paddingX) - left;
    const boxHeight = Math.min(height, originalTop + originalHeight + paddingY) - top;
    const context = box.detected === false;
    const color = context ? '#00c8ff' : '#ffff00';
    return <g key={index}><rect data-testid="motion-box" x={left} y={top} width={boxWidth} height={boxHeight} fill="none" stroke={color} strokeWidth={context ? 0.75 : 1} vectorEffect="non-scaling-stroke" />
      {originalWidth >= 55 && <text x={left + 2} y={top >= 15 ? top - 4 : top + 13} fontSize="12" fill={color} stroke="#071005" strokeWidth="0.5" paintOrder="stroke">{context ? 'CONTEXT' : 'MOTION'}</text>}</g>;
  })}</svg>;
  if (['deleting', 'deleted'].includes(task.mediaState) || task.mediaDeletedAt) return <div className="cr-media-cleaned" role="status">
    <strong>{task.mediaState === 'deleting' ? 'Media cleanup in progress' : 'Media cleaned up'}</strong>
    <p>The review history and final decision have been kept. This review is read-only.</p>
  </div>;
  return <>
    {!previewOnly && (showSpottedTime || compact) && <div className="cr-motion-evidence-meta">
      {showSpottedTime && <MotionSpottedTime capturedAt={task.capturedAt} compact={compact} />}
      {compact && <div className="cr-motion-mobile-tools" role="group" aria-label="Motion preview options">
        <button type="button" aria-label={boxesShown ? 'Hide boxes' : 'Show boxes'} title={boxesShown ? 'Hide boxes' : 'Show boxes'} aria-pressed={boxesShown} onClick={() => { trackCommunity('motion_boxes_toggled', { boxes_visible: !boxesShown }); setBoxesShown(!boxesShown); }}>Boxes</button>
        <button type="button" aria-label={nativeControlsShown ? 'Hide video controls' : 'Show video controls'} title={nativeControlsShown ? 'Hide video controls' : 'Show video controls'} aria-pressed={nativeControlsShown} onClick={toggleControls}>Controls</button>
      </div>}
    </div>}
    <div className={`cr-motion-previews ${compact ? 'cr-motion-previews--compact' : ''}${useDeck ? ' cr-motion-previews--deck' : ''}`} style={{ '--cr-motion-aspect': `${width} / ${height}` }}>
      <figure className="cr-motion-panel">
        {!compact && <figcaption>Preview with boxes</figcaption>}
        <div className="cr-motion-video cr-motion-stage">
          {useDeck ? <MotionVideoDeck task={task} upcoming={upcomingTasks} bindVideo={bindVideo} videoProps={videoProps} playbackRate={speed}>{boxOverlay}</MotionVideoDeck>
            : <><video {...videoProps} ref={bindVideo} src={task.videoUrl} />{boxOverlay}</>}
          {compact && !nativeControlsShown && playingFor !== mediaKey && <button type="button" className="cr-motion-play" onClick={playClip} aria-label="Play video">
            <span><FiPlay aria-hidden="true" />Play video</span>
          </button>}
        </div>
      </figure>
      {!compact && <><figure className="cr-motion-panel"><figcaption>Preview without boxes</figcaption><div className="cr-motion-stage"><canvas ref={cleanPreview} width={width} height={height} role="img" aria-label="Clean motion preview" /></div></figure>
        <figure className="cr-motion-panel"><figcaption>Motion mask</figcaption><div className="cr-motion-stage">
          {task.maskVideoUrl ? <>
            <video ref={maskVideo} src={task.maskVideoUrl} aria-label="Motion mask" muted playsInline loop disablePictureInPicture preload={autoPlay ? 'auto' : 'metadata'}
              onLoadedMetadata={() => { applyMotionPlaybackRate(maskVideo.current, getMotionPlaybackRate()); synchronizeMask({ forceSeek: true }); }}
              onLoadedData={() => { setMaskReady(true); setMaskFailed(false); synchronizeMask({ forceSeek: true }); }}
              onCanPlay={() => synchronizeMask()} onSeeked={() => synchronizeMask()} onError={() => { setMaskFailed(true); setMaskReady(false); onMaskError?.(); }} />
            {(!maskReady || maskFailed) && <div className="cr-motion-mask-status" role="status">{maskFailed ? <>Motion mask couldn’t load.{onRefresh && <button type="button" className="cr-text-button" onClick={onRefresh}>Refresh clip</button>}</> : 'Loading motion mask…'}</div>}
          </> : <div className="cr-motion-mask-status">Motion mask not available for this clip.</div>}
        </div></figure></>}
    </div>
    {playErrorFor === mediaKey && <p className="cr-error" role="alert">This video couldn’t start. {onRefresh && <button type="button" className="cr-text-button" onClick={onRefresh}>Refresh clip</button>}</p>}
    {!previewOnly && <MotionTrackReference key={`reference-${task.id}`} trackId={task.id} compact={compact && showSpottedTime} reviewedCount={reviewedCount} accuracy={accuracy} />}
    {!previewOnly && (!compact || !showSpottedTime || nativeControlsShown) && <div className="cr-motion-video-tools"><label>Playback speed<select value={speed} onChange={(event) => { const value = Number(event.target.value); trackCommunity('motion_speed_changed', { playback_speed: value }); setPlaybackSpeed(value); }}><option value="0.5">0.5×</option><option value="1">1×</option><option value="2">2×</option><option value="2.5">2.5×</option><option value="5">5×</option>{![0.5, 1, 2, 2.5, 5].includes(speed) && <option value={speed}>{speed}×</option>}</select></label>
      {onEnlarge && <button type="button" className="cr-text-button" onClick={() => { trackCommunity('motion_clip_enlarged'); cancelAutoplay(); video.current?.pause(); onEnlarge(); }}><FiMaximize2 aria-hidden="true" /> Enlarge clip</button>}
      {allowFullClip && !task.fullClipUrl && <span className="cr-muted">No full clip available</span>}
    </div>}
    {!previewOnly && allowFullClip && task.fullClipUrl && <InlineFullClip key={task.id} url={task.fullClipUrl} onRefresh={onRefresh} onPlay={() => { cancelAutoplay(); video.current?.pause(); pauseMask(); }} />}
  </>;
}

export function MotionViewer({ task, onClose, onRefresh }) {
  const dialog = useRef(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.querySelector('button')?.focus();
    const keyDown = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); }
      if (event.key !== 'Tab') return;
      const items = [...dialog.current.querySelectorAll('button:not(:disabled), input, select, a[href], video[controls]')];
      const first = items[0]; const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keyDown);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', keyDown); previousFocus?.focus(); };
  }, [onClose]);
  return <div className="cr-image-modal" role="dialog" aria-modal="true" aria-label="Enlarged motion clip" ref={dialog}>
    <div className="cr-image-toolbar"><span>Take a closer look at the movement</span><button type="button" className="cr-icon-button" aria-label="Close enlarged motion clip" onClick={onClose}><FiX aria-hidden="true" /></button></div>
    <div className="cr-motion-viewer-body"><MotionEvidence task={task} onRefresh={onRefresh} onError={() => setFailed(true)} onLoad={() => setFailed(false)} />{failed && <p className="cr-error" role="alert">This clip couldn’t load. <button className="cr-text-button" onClick={onRefresh}>Refresh clip</button></p>}</div>
  </div>;
}

export default function MotionReviewScreen(props) {
  const api = props.api || motionReviewApi;
  return api.buffer ? <BufferedMotionReviewScreen key={typeof api.scope === 'function' ? api.scope() : api.scope} {...props} api={api} /> : <LegacyMotionReviewScreen {...props} api={api} />;
}

function LegacyMotionReviewScreen({ api, onCountChange, demo = false, onBusyChange, showLeaderboard = false, reviewedCount }) {
  const [accuracy, setAccuracy] = useState(null);
  const compact = useCompactMotionView();
  const { celebration, celebrate, dismissCelebration } = useMotionCelebration();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [generation, setGeneration] = useState(0);
  const [retryPending, setRetryPending] = useState(false);
  const [lastReview, setLastReview] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showEnlarged, setShowEnlarged] = useState(false);
  const mounted = useRef(false);
  const revision = useRef(0);
  const busy = useRef(false);
  const attempt = useRef(null);
  const undoAttempt = useRef(null);
  const currentTask = useRef(task);
  const onCount = useRef(onCountChange);
  currentTask.current = task;
  onCount.current = onCountChange;
  useEffect(() => { onBusyChange?.(saving || undoing || retryPending); }, [onBusyChange, retryPending, saving, undoing]);

  const loadNext = useCallback(async ({ preserveAnswer = false } = {}) => {
    const currentRevision = ++revision.current;
    setLoading(true); setError('');
    try {
      let result;
      do {
        result = await api.next();
        if (!mounted.current || currentRevision !== revision.current) return;
      } while (!result.task && result.hasMore);
      const sameTask = result.task && result.task.id === currentTask.current?.id;
      const sameAssignment = sameTask && result.task.undoToken === currentTask.current?.undoToken;
      if (!sameAssignment) { attempt.current = null; setRetryPending(false); }
      if (preserveAnswer && !sameTask) setNotice('This clip is no longer open. Your next available clip is ready.');
      else if (preserveAnswer && !sameAssignment) setNotice('This clip was restored by Undo. Please review it again.');
      setTask(result.task || null); setLoaded(false); setFailed(false); setGeneration((value) => value + 1);
      setLastReview(result.lastSubmission || null);
      if (undoAttempt.current?.submissionId !== result.lastSubmission?.submissionId) undoAttempt.current = null;
      onCount.current?.(Number(result.reviewedCount) || 0);
    } catch (requestError) {
      if (mounted.current && currentRevision === revision.current) setError(readableError(requestError));
    } finally { if (mounted.current && currentRevision === revision.current) setLoading(false); }
  }, [api]);
  useEffect(() => {
    mounted.current = true;
    loadNext();
    return () => { mounted.current = false; revision.current += 1; };
  }, [loadNext]);

  const submit = useCallback(async (verdict, flaggedAs = null) => {
    if (!task || busy.current || loading || failed || !loaded || (retryPending && (flaggedAs || attempt.current?.verdict !== verdict))) return;
    busy.current = true; setSaving(true); setError(''); setNotice('');
    if (!attempt.current) {
      const submittedFlags = { ...noFlags(), ...(flaggedAs ? { [flaggedAs]: true } : {}) };
      attempt.current = { taskId: task.id, requestId: requestId(), verdict, speciesId: null, flags: submittedFlags, ...(task.undoToken ? { undoToken: task.undoToken } : {}) };
    }
    const submittedAnswer = attempt.current;
    try {
      const result = await api.submit(submittedAnswer);
      if (!mounted.current) return;
      if (result.accepted === false && result.reason === 'submission-undone') {
        attempt.current = null; setRetryPending(false); setTask(null);
        setNotice('That review was undone in another session. Your current clip has been refreshed.');
        await loadNext(); return;
      }
      if (result.accepted !== true && !(result.accepted === false && result.reason === 'task-closed')) {
        attempt.current = null; setRetryPending(false); setError('This review was not saved. Refresh its status before trying again.'); return;
      }
      if (typeof result.reviewedCount === 'number') onCount.current?.(result.reviewedCount);
      if (result.accepted === true) celebrate(submittedAnswer);
      setNotice(result.accepted === false ? 'This clip was completed while you were reviewing it. Here’s the next one.' : 'Motion review saved. Thank you!');
      if (result.lastSubmission) setLastReview(result.lastSubmission);
      attempt.current = null; setRetryPending(false); setTask(null);
      await loadNext();
      window.scrollTo?.({ top: 0, behavior: 'instant' });
    } catch (requestError) {
      if (mounted.current) {
        const uncertain = !requestError?.code || ['functions/unavailable', 'functions/deadline-exceeded', 'functions/internal'].includes(requestError.code);
        setRetryPending(uncertain);
        if (!uncertain) attempt.current = null;
        setError(readableError(requestError));
      }
    } finally { busy.current = false; if (mounted.current) setSaving(false); }
  }, [api, celebrate, failed, loadNext, loaded, loading, retryPending, task]);

  const undo = useCallback(async () => {
    if (!lastReview || busy.current || loading || retryPending) return;
    dismissCelebration();
    busy.current = true; setUndoing(true); setError(''); setNotice('');
    if (!undoAttempt.current || undoAttempt.current.submissionId !== lastReview.submissionId) undoAttempt.current = { taskId: lastReview.taskId, submissionId: lastReview.submissionId, requestId: requestId() };
    try {
      const result = await api.undo(undoAttempt.current);
      if (!mounted.current) return;
      if (result.undone !== true) { setError('The previous review could not be undone. Refresh its status before trying again.'); return; }
      if (typeof result.reviewedCount === 'number') onCount.current?.(result.reviewedCount);
      attempt.current = null; undoAttempt.current = null; setRetryPending(false); setLastReview(result.lastSubmission || null); setTask(null);
      setNotice('Last motion review undone. Please check this clip again.');
      await loadNext();
    } catch (requestError) { if (mounted.current) setError(readableError(requestError)); }
    finally { busy.current = false; if (mounted.current) setUndoing(false); }
  }, [api, dismissCelebration, lastReview, loadNext, loading, retryPending]);

  useEffect(() => {
    const keyDown = (event) => {
      if (event.defaultPrevented || event.repeat || event.isComposing || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || isTyping(event.target) || busy.current || loading || showEnlarged) return;
      const key = event.code === 'Space' || event.key === 'Spacebar' ? ' ' : event.key?.toLowerCase();
      if ((key === 's' || key === 'n') && task && loaded && !failed && !retryPending) { event.preventDefault(); submit(key === 's' ? 'animal' : 'false_alarm'); }
      if ((key === 'p' || key === 'h') && task && loaded && !failed && !retryPending) { event.preventDefault(); submit('animal', key === 'p' ? 'predator' : 'interesting'); }
      if (key === ' ' && task && loaded && !failed && !retryPending) { event.preventDefault(); submit('unsure'); }
      if (key === 'u' && lastReview && !retryPending) { event.preventDefault(); undo(); }
    };
    document.addEventListener('keydown', keyDown);
    return () => document.removeEventListener('keydown', keyDown);
  }, [failed, lastReview, loaded, loading, retryPending, showEnlarged, submit, task, undo]);

  const operating = saving || undoing;
  const closeEnlarged = useCallback(() => setShowEnlarged(false), []);
  const refresh = () => { if (!busy.current && !loading) { setShowEnlarged(false); return loadNext({ preserveAnswer: true }); } };
  const undoButton = <button className="cr-undo" type="button" aria-label="Undo last motion review" aria-keyshortcuts="U" disabled={!lastReview || operating || loading || retryPending} onClick={undo}><FiCornerUpLeft aria-hidden="true" />{undoing ? 'Undoing…' : 'Undo'} <kbd aria-hidden="true">U</kbd></button>;
  return <main className="cr-main cr-motion-main">
    <MotionCelebration celebration={celebration} />
    <div className="cr-intro"><h1>Help us spot real movement</h1><MotionReviewRules compact={compact} /></div>
    {showLeaderboard && <MotionLeaderboard onAccuracyChange={setAccuracy} api={api} reviewedCount={reviewedCount} compact={compact} hidePersonalCount={compact && Boolean(task)} />}
    <MotionReviewNotice notice={notice} />
    {demo && <p className="cr-demo-label">Design preview · Reviews stay in this browser and are not submitted.</p>}
    {loading ? <div className="cr-empty" role="status"><h2>Finding your next motion clip…</h2></div> : !task ? <div className="cr-empty">
      <h2>{error ? 'We couldn’t load your next clip' : 'You’re caught up on motion'}</h2>
      {error ? <p className="cr-error" role="alert">{error}</p> : <p>You’ve reviewed all the motion clips currently available to you. Come back soon for more.</p>}
      <div className="cr-empty-actions"><button className="cr-primary" disabled={operating} onClick={() => loadNext()}><FiRefreshCw aria-hidden="true" />{error ? 'Try again' : 'Check for new clips'}</button>{undoButton}</div>
    </div> : <div className="cr-review-layout">
      <section className="cr-photo-section" aria-label="Motion evidence">
        <MotionEvidence key={`${task.id}-${generation}`} task={task} showSpottedTime accuracy={accuracy} reviewedCount={showLeaderboard ? reviewedCount : undefined} onRefresh={refresh} onEnlarge={() => setShowEnlarged(true)} onLoad={() => { setLoaded(true); setFailed(false); }} onError={() => { setFailed(true); setLoaded(false); }} />
        <p className="cr-motion-hint"><span className="cr-motion-desktop-hint">Count any animal or bird moving on the ground or in a tree, including outside the highlighted box. </span>The clip repeats so you can take another look.</p>
        {!loaded && !failed && <p className="cr-small cr-muted" role="status">Loading the clip…</p>}
        {failed && <p className="cr-error" role="alert">This clip couldn’t load. <button className="cr-text-button" onClick={refresh} disabled={operating}>Refresh clip</button></p>}
      </section>
      <MotionAnswerPanel>
        {!compact && <MotionFlagActions disabled={operating || retryPending || !loaded || failed} onFlag={(flag) => submit('animal', flag)} />}
        <div className="cr-motion-submit" role="group" aria-label="Motion review controls">
          {error && <p className="cr-error" role="alert">{error}</p>}
          {retryPending && <><p className="cr-small cr-muted">Your answer is kept. Retry it or refresh its status before changing your answer.</p><button type="button" className="cr-primary" disabled={operating || !loaded || failed} onClick={() => submit(attempt.current.verdict)}>Retry motion review</button></>}
          {error && <button type="button" className="cr-text-button" onClick={refresh} disabled={operating}>Refresh review status</button>}
          {compact ? <MotionMobileActions disabled={operating || retryPending || !loaded || failed} onSubmit={submit} undoButton={undoButton} /> : <>
          <div className="cr-motion-verdicts" role="group" aria-label="Motion review answer">
            {[['animal', 'Real motion', 'S'], ['false_alarm', 'False alarm', 'N'], ['unsure', 'Not sure', 'Space']].map(([value, label, shortcut]) => <button className={`cr-verdict ${value === 'animal' ? 'cr-motion-animal' : ''}`} type="button" key={value} aria-keyshortcuts={shortcut} disabled={operating || retryPending || !loaded || failed} onClick={() => submit(value)}>{label}<kbd aria-hidden="true">{shortcut}</kbd></button>)}
          </div>
          <div className="cr-motion-bottom"><span className="cr-small cr-muted">{saving ? 'Saving…' : 'Unsure? Don’t guess.'}</span>{undoButton}</div>
          </>}
        </div>
      </MotionAnswerPanel>
    </div>}
    {showEnlarged && task && <MotionViewer task={task} onClose={closeEnlarged} onRefresh={refresh} />}
  </main>;
}
